import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { appRoute } from "./route";
import { createResource, createSignal, Show } from "solid-js";
import { Bell, BellOff, BellRing, CheckCircle, Mail } from "@fleet/icons";
import { InvitationList } from "@/features/organizations/components/invitation-list";
import { useI18n } from "@/providers/i18n-provider";
import {
  getNotificationPermission,
  requestNotificationPermission,
  showNotification,
} from "@/lib/notifications/service";

function NotificationsPage() {
  const { t } = useI18n();

  const [permission, { refetch: refetchPermission }] = createResource(getNotificationPermission);
  const [testSent, setTestSent] = createSignal(false);

  const handleRequestPermission = async () => {
    await requestNotificationPermission();
    await refetchPermission();
  };

  const permissionLabel = () => {
    const labels: Record<string, string> = {
      granted: t(msg`Notifications enabled`),
      denied: t(msg`Notifications blocked`),
      unsupported: t(msg`Not supported on this platform`),
    };
    return labels[permission() ?? ""] ?? t(msg`Permission not requested yet`);
  };

  const handleTestNotification = async () => {
    await showNotification(
      // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n)
      "Bible Strong Offline",
      t(msg`System notifications are working correctly.`),
    );
    setTestSent(true);
  };

  return (
    <div class="flex flex-col h-full w-full bg-background text-foreground overflow-hidden">
      {/* Header */}
      <div class="border-b border-border/40 bg-card/30 backdrop-blur-lg px-4 md:px-8 py-4 md:py-5 flex items-center gap-3 shrink-0">
        <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
          <Bell class="h-5 w-5" />
        </div>
        <div>
          <h1 class="text-xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/75 bg-clip-text text-transparent">
            {t(msg`Notifications`)}
          </h1>
          <p class="text-xs text-muted-foreground mt-0.5">
            {t(msg`Pending invitations and system notification preferences`)}
          </p>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-4 md:p-8">
        <div class="max-w-3xl mx-auto w-full space-y-10">
          {/* Pending invitations (in-app notifications) */}
          <section class="space-y-4">
            <div class="flex items-center gap-3">
              <Mail class="h-5 w-5 text-primary" />
              <h2 class="text-base font-bold text-foreground">{t(msg`Invitations`)}</h2>
            </div>
            <InvitationList />
          </section>

          {/* System notification permission */}
          <section class="space-y-4">
            <div class="flex items-center gap-3">
              <BellRing class="h-5 w-5 text-primary" />
              <h2 class="text-base font-bold text-foreground">{t(msg`System notifications`)}</h2>
            </div>
            <p class="text-sm text-muted-foreground leading-relaxed">
              {t(
                msg`Allow the app to send notifications from your device (reminders and sync events).`,
              )}
            </p>

            <div class="flex items-center justify-between gap-4 p-5 rounded-2xl border border-border/30 bg-card/40">
              <div class="flex items-center gap-3">
                <Show
                  when={permission() === "granted"}
                  fallback={<BellOff class="h-5 w-5 text-muted-foreground" />}
                >
                  <CheckCircle class="h-5 w-5 text-emerald-500" />
                </Show>
                <div>
                  <p class="text-sm font-bold text-foreground">{permissionLabel()}</p>
                  <Show when={permission() === "denied"}>
                    <p class="text-xs text-muted-foreground mt-0.5">
                      {t(
                        msg`Enable notifications for this app in your system or browser settings.`,
                      )}
                    </p>
                  </Show>
                </div>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <Show when={permission() === "default"}>
                  <button
                    type="button"
                    onClick={() => {
                      void handleRequestPermission();
                    }}
                    class="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-semibold rounded-xl transition-all shadow-sm active:scale-95"
                  >
                    {t(msg`Enable`)}
                  </button>
                </Show>
                <Show when={permission() === "granted"}>
                  <button
                    type="button"
                    onClick={() => {
                      void handleTestNotification();
                    }}
                    class="px-4 py-2 bg-background border border-border/40 hover:bg-muted/40 text-xs font-semibold rounded-xl transition-all active:scale-95"
                  >
                    {testSent() ? t(msg`Sent!`) : t(msg`Send test`)}
                  </button>
                </Show>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/notifications",
  component: NotificationsPage,
});
